import { Dayjs } from "dayjs";

import {
  ALERT_CLASSIFICATION,
  LOCATION_CLASSIFICATION,
} from "@/components/alerts/constants";
import { IAssociatedCrime, IPoliceService, PlaceType } from "@/types";

import { ImageTagGroup } from "./imageTags";

/**
 * The form model for add-v2.
 *
 * This is the single source of truth while a bulletin is being written. It is deliberately NOT the
 * API payload shape — `model/adapters.ts` converts in both directions. See README decision 1.
 */

/* -------------------------------------------------------------------------- */
/* Entities                                                                    */
/* -------------------------------------------------------------------------- */

export const ENTITY_TYPES = ["person", "vehicle", "item"] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

/**
 * Involvement is required on all three entity types, and the option sets do not overlap.
 * "Person of interest", "Victim", "Witness", "Driver", "Passenger" and "Owner" were deliberately
 * removed from the person list during the 2026-07-28 design review.
 */
export const PERSON_INVOLVEMENTS = [
  "wanted",
  "arrested",
  "suspect",
  "subject",
  "missing",
  "other",
] as const;
export type PersonInvolvement = (typeof PERSON_INVOLVEMENTS)[number];

export const VEHICLE_INVOLVEMENTS = [
  "suspect",
  "stolen",
  "to_locate",
  "other",
] as const;
export type VehicleInvolvement = (typeof VEHICLE_INVOLVEMENTS)[number];

export const ITEM_INVOLVEMENTS = [
  "stolen",
  "found",
  "seized",
  "to_locate",
  "other",
] as const;
export type ItemInvolvement = (typeof ITEM_INVOLVEMENTS)[number];

interface EntityBase {
  /** Client-side only. React key, and the target media items point at. */
  id: string;
  /**
   * True while this entity is an untouched placeholder seeded from the bulletin type. Changing the
   * bulletin type re-seeds only entities still marked `autoAdded`; anything edited is left alone.
   */
  autoAdded: boolean;
  /** Free text. Placeholder copy: "E.g. clothing, visuals, etc." */
  additionalDescription: string;
  /** Only meaningful when `involvement` is "other". */
  otherInvolvement: string;
}

export interface PersonEntity extends EntityBase {
  type: "person";
  involvement: PersonInvolvement | null;
  firstName: string;
  lastName: string;
  /** ISO calendar date (`YYYY-MM-DD`), no time component. */
  dateOfBirth: string | null;
}

export interface VehicleEntity extends EntityBase {
  type: "vehicle";
  involvement: VehicleInvolvement | null;
  /**
   * A closed vocabulary: an NHTSA vPIC make name, or one of the two sentinels
   * `VEHICLE_UNKNOWN` / `VEHICLE_OTHER`. Free text lives in `makeOther` and only when the value is
   * `other` — keeping this column closed is what makes the search facet reliable.
   */
  make: string;
  /** Only meaningful when `make` is `other`. */
  makeOther: string;
  /** As `make`. A model belongs to a make, so changing the make clears this. */
  model: string;
  /** Only meaningful when `model` is `other`. */
  modelOther: string;
  /**
   * A key from `VEHICLE_COLOURS`, or `VEHICLE_OTHER`. A key rather than the English name for the
   * reason the makes are not keys: a colour has a French name, and one stored value has to render
   * in both languages.
   */
  colour: string;
  /** Only meaningful when `colour` is `other`. */
  colourOther: string;
  /** As `colour`, over `VEHICLE_TYPES`. */
  vehicleType: string;
  /** Only meaningful when `vehicleType` is `other`. */
  vehicleTypeOther: string;
  licencePlate: string;
  jurisdiction: string;
}

export interface ItemEntity extends EntityBase {
  type: "item";
  involvement: ItemInvolvement | null;
  itemName: string;
}

/**
 * Discriminated union — README decision 10. Switching an entity's type rebuilds the object rather
 * than mutating it, which is why stale fields cannot survive a type change.
 *
 * Note there is no `attachments` field here: the entity/media relation is stored once, on the media
 * item (README decision 13). An entity's media strip is derived.
 */
export type BulletinEntity = PersonEntity | VehicleEntity | ItemEntity;

/* -------------------------------------------------------------------------- */
/* Locations                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Written once, when the user picks an address from the autocomplete — never during validation.
 * README decision 3.
 */
export interface ResolvedPlace {
  placeId: string;
  formattedAddress: string;
  addressComponents: google.maps.GeocoderAddressComponent[];
  geometry: google.maps.LatLngLiteral;
  locationType: string;
  types: string[];
  plusCode: google.maps.GeocoderResult["plus_code"];
}

export interface BulletinLocation {
  id: string;
  /**
   * What the autocomplete prediction showed. Kept alongside `resolved` because the geocoder can
   * return a different place than the prediction (e.g. "Greater Sudbury"), and the display string
   * users expect is the prediction's.
   */
  input: PlaceType | null;
  /** `null` until the address resolves. Type stays editable meanwhile — the design requires it. */
  resolved: ResolvedPlace | null;
  classification: LOCATION_CLASSIFICATION | null;
  /** Only meaningful when `classification` is `OTHER`. */
  otherClassification: string;
  unit: string;
}

/* -------------------------------------------------------------------------- */
/* Incident times                                                              */
/* -------------------------------------------------------------------------- */

export const INCIDENT_TIME_TYPES = [
  "exact",
  "time_unknown",
  "range",
  "not_applicable",
  "unknown",
] as const;
export type IncidentTimeType = (typeof INCIDENT_TIME_TYPES)[number];

export interface BulletinIncidentTime {
  id: string;
  timeType: IncidentTimeType;
  startDate: Dayjs | null;
  /** Only used when `timeType` is "range". */
  endDate: Dayjs | null;
}

/* -------------------------------------------------------------------------- */
/* Contacts                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Unlike the legacy `IContact`, this carries no `error` flag — validation state belongs to the form
 * library, not to the value.
 */
export interface BulletinContact {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  extension: string;
  badgeNumber: string;
}

/* -------------------------------------------------------------------------- */
/* Media                                                                       */
/* -------------------------------------------------------------------------- */

export interface BulletinMediaItem {
  id: string;
  filename: string;
  contentType: string;
  /** Absolute download URL on the API, as the attachment endpoints return it. */
  url: string;
  /** Images only — the API generates no thumbnail for a video or any other file. */
  thumbnailUrl: string | null;
  sortOrdinal: number;
  /**
   * Ids of the entities this file is attached to. This is the only place the entity/media relation
   * is stored — see the note on `BulletinEntity`.
   */
  entityIds: string[];
  description: string;
  /**
   * The AI tags, generated on upload and **read-only here** — nothing in this form writes them.
   * Empty means the model could not read the image, which the card says out loud; a video is never
   * asked. See `model/imageTags.ts`.
   */
  tagGroups: ImageTagGroup[];
}

export interface BulletinPdf {
  filename: string;
  contentType: string;
  /**
   * The API's own `download_url`, which for the PDF is a **path** (`/bulletin/{id}/pdf`), not an
   * absolute URL as it is for attachments. `DisplayAttachedPdf` is what resolves it against the
   * server, so it is stored exactly as received.
   */
  url: string;
}

/* -------------------------------------------------------------------------- */
/* The form                                                                    */
/* -------------------------------------------------------------------------- */

export type BulletinLanguage = "en" | "fr";

export interface BulletinFormValues {
  /* Setup */
  policeService: IPoliceService | null;
  /**
   * `reference_name` of a bulletin type. The list is admin-managed through `/bulletin/types`, so
   * this cannot be a closed union. `BULLETIN_TYPE` stays the canonical set that entity seeding keys
   * off — the design flagged that its own mockup values had drifted from it.
   */
  bulletinType: string | null;
  occurrenceNumbers: string[];
  /** Only required when the selected police service defines operational areas. */
  operationalArea: string | null;

  /* Incident */
  associatedCrimes: IAssociatedCrime[];
  incidentTimes: BulletinIncidentTime[];
  locations: BulletinLocation[];

  /* People, vehicles & items */
  entities: BulletinEntity[];

  /* Media */
  media: BulletinMediaItem[];
  pdf: BulletinPdf | null;

  /* Narrative */
  description: string;
  descriptionFr: string;
  originalLanguage: BulletinLanguage;

  /* Contacts & approval */
  contacts: BulletinContact[];
  approvedBy: string;
  approvedByBadge: string;
  classification: ALERT_CLASSIFICATION | null;
  /** "Advanced" — the issued date/time override at the end of the contacts section. */
  issuedTime: Dayjs | null;
}

/**
 * Draft identity and `is_draft` are deliberately absent from the form model: they belong to the
 * repository, which distinguishes saving from finalizing. See README decisions 5 and 6.
 */
export type BulletinFormField = keyof BulletinFormValues;
